import { Link } from 'react-router-dom';
import {
    Facebook,
    Twitter,
    Instagram,
    Linkedin,
    Mail,
    Shield,
    Award,
    TrendingUp,
    Download,
    QrCode
} from 'lucide-react';
import DiasporawiseLogo from '../../assets/DWBRANDprview.png';

const Footer = () => {
    const currentYear = new Date().getFullYear();

    const footerSections = [
        {
            title: "Company",
            links: [
                { name: "Home", href: "/" },
                { name: "About Us", href: "/about" },
                { name: "Blogs & Insights", href: "/blogs" },
                { name: "Careers", href: "/careers" },
                { name: "Contact Us", href: "/contact" },
            ]
        },
        {
            title: "Invest",
            links: [
                { name: "Browse Projects", href: "/projects" },
                { name: "Farm Sponsorship", href: "/farm-sponsorship" },
                { name: "Real Estate Investment", href: "/investment/real-estate-investment-nigeria" },
                { name: "Real Estate Abroad", href: "/investment/real-estate-investing-from-abroad" },
                { name: "Commercial Property", href: "/investment/commercial-property-investment-nigeria" },
                { name: "Fractional Investment", href: "/investment/fractional-property-investment-nigeria" }
            ]
        },
        {
            title: "Diaspora Portals",
            links: [
                { name: "Diaspora Hub", href: "/nigerian-diaspora-investors" },
                { name: "United Kingdom", href: "/nigerian-diaspora-investors/uk" },
                { name: "United States", href: "/nigerian-diaspora-investors/us" },
                { name: "Europe Region", href: "/nigerian-diaspora-investors/europe" },
                { name: "Asia Region", href: "/nigerian-diaspora-investors/asia" }
            ]
        },
        {
            title: "Marketplace & Services",
            links: [
                { name: "Marketplace", href: "/marketplace" },
                { name: "Solar Energy", href: "/solar" },
                { name: "Short Stay Apartments", href: "/apartments" }
            ]
        },
        {
            title: "Legal",
            links: [
                { name: "Privacy Policy", href: "/privacy-policy" },
                { name: "Terms of Service", href: "/terms-of-service" }
            ]
        }
    ];

    const socialLinks = [
        { name: "Facebook", icon: Facebook, href: "#", color: "hover:bg-blue-600" },
        { name: "Twitter", icon: Twitter, href: "#", color: "hover:bg-sky-500" },
        { name: "Instagram", icon: Instagram, href: "#", color: "hover:bg-pink-600" },
        { name: "LinkedIn", icon: Linkedin, href: "#", color: "hover:bg-blue-700" }
    ];

    const contactInfo = [
        { icon: Mail, text: "info@diasporawise.com", href: "mailto:info@diasporawise.com" }
    ];

    return (
        <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
            {/* Main Footer Content */}
            <div className="container mx-auto px-6 py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-8 lg:gap-6">
                    {/* Brand Section */}
                    <div className="md:col-span-2 lg:col-span-2">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-20 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg flex items-center justify-center flex-shrink-0">
                                <img
                                    src={DiasporawiseLogo}
                                    alt="Diasporawise Logo"
                                    className="h-10 w-14"
                                />
                            </div>
                            <div className="min-w-0">
                                <h3 className="text-xl font-bold">
                                    <span className="text-white">DIASPORA</span>
                                    <span className="text-blue-400">WISE</span>
                                </h3>
                                <p className="text-sm text-gray-400">Investment Platform</p>
                            </div>
                        </div>

                        <p className="text-gray-300 mb-6 leading-relaxed">
                            Diasporawise is a premium investment platform offering secure,
                            transparent, and profitable opportunities for investors worldwide.
                        </p>

                        {/* Trust Indicators */}
                        <div className="grid grid-cols-3 gap-3 mb-8">
                            <div className="text-center p-3 bg-white/5 rounded-xl backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-300">
                                <Shield className="w-5 h-5 text-blue-400 mx-auto mb-2" />
                                <div className="text-sm font-semibold text-white">Secure</div>
                                <div className="text-xs text-gray-400">Bank-grade</div>
                            </div>
                            <div className="text-center p-3 bg-white/5 rounded-xl backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-300">
                                <Award className="w-5 h-5 text-blue-400 mx-auto mb-2" />
                                <div className="text-sm font-semibold text-white">Trusted</div>
                            </div>
                            <div className="text-center p-3 bg-white/5 rounded-xl backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-300">
                                <TrendingUp className="w-5 h-5 text-blue-400 mx-auto mb-2" />
                                <div className="text-sm font-semibold text-white">Growing</div>
                            </div>
                        </div>

                        {/* Social Links */}
                        <div className="flex gap-3">
                            {socialLinks.map((social, index) => {
                                const IconComponent = social.icon;
                                return (
                                    <a
                                        key={index}
                                        href={social.href}
                                        className={`w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center text-gray-300 hover:text-white transition-all duration-300 ${social.color} backdrop-blur-sm hover:scale-110 border border-white/10`}
                                        aria-label={social.name}
                                    >
                                        <IconComponent className="w-5 h-5" />
                                    </a>
                                );
                            })}
                        </div>
                    </div>

                    {/* Footer Links */}
                    {footerSections.map((section, index) => (
                        <div key={index} className="flex flex-col">
                            <h4 className="text-lg font-semibold mb-6 text-white">{section.title}</h4>
                            <ul className="space-y-3 flex-1">
                                {section.links.map((link, linkIndex) => (
                                    <li key={linkIndex}>
                                        <Link
                                            to={link.href}
                                            className="text-gray-300 hover:text-blue-400 transition-colors duration-300 text-sm py-1 block hover:translate-x-1"
                                        >
                                            <span className="truncate">{link.name}</span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Contact & Download Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mt-16 pt-8 border-t border-gray-700">
                    {/* Contact Information */}
                    <div className="flex flex-col">
                        <h4 className="text-lg font-semibold mb-6 text-white">Get in Touch</h4>
                        <div className="space-y-4 flex-1">
                            {contactInfo.map((contact, index) => {
                                const IconComponent = contact.icon;
                                return (
                                    <a
                                        key={index}
                                        href={contact.href}
                                        className="flex items-center gap-3 text-gray-300 hover:text-blue-400 transition-colors duration-300 group py-2"
                                    >
                                        <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center group-hover:bg-blue-500/20 transition-colors duration-300 flex-shrink-0">
                                            <IconComponent className="w-4 h-4" />
                                        </div>
                                        <span className="text-sm truncate">{contact.text}</span>
                                    </a>
                                );
                            })}
                        </div>
                    </div>

                    {/* Download App Section */}
                    <div className="lg:hidden flex flex-col">
                        <h4 className="text-lg font-semibold mb-6 text-white">Download Our App</h4>
                        <p className="text-gray-300 mb-6">
                            Get the full Diasporawise experience on your mobile device.
                        </p>

                        <div className="space-y-4 flex-1">
                            <a
                                href="https://app.diasporawise.com"
                                className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-all duration-300 group border border-white/10"
                            >
                                <Download className="w-6 h-6 text-blue-400 flex-shrink-0" />
                                <div className="min-w-0">
                                    <div className="font-semibold text-white">Download Mobile App</div>
                                    <div className="text-sm text-gray-400">Available on iOS & Android</div>
                                </div>
                            </a>

                            <a
                                href="https://app.diasporawise.com"
                                className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-all duration-300 group border border-white/10"
                            >
                                <QrCode className="w-6 h-6 text-blue-400 flex-shrink-0" />
                                <div className="min-w-0">
                                    <div className="font-semibold text-white">Scan QR Code</div>
                                    <div className="text-sm text-gray-400">Quick download access</div>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-gray-700">
                <div className="container mx-auto px-6 py-6">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="text-gray-400 text-sm text-center md:text-left">
                            <span>© {currentYear} Diasporawise. All rights reserved.</span>
                            <span className="hidden sm:inline mx-2">|</span>
                            <span className="block sm:inline mt-1 sm:mt-0">Designed with ❤️ for investors worldwide</span>
                        </div>

                        <div className="flex items-center gap-4 md:gap-6 text-sm">
                            <span className="text-gray-400 hidden sm:inline">Follow us:</span>
                            <div className="flex gap-2">
                                {socialLinks.map((social, index) => {
                                    const IconComponent = social.icon;
                                    return (
                                        <a
                                            key={index}
                                            href={social.href}
                                            className="text-gray-400 hover:text-blue-400 transition-colors duration-300 p-2 rounded-lg hover:bg-white/10 hover:scale-110"
                                            aria-label={social.name}
                                        >
                                            <IconComponent className="w-4 h-4" />
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;